// ProcessTimeline.js - Australia
import React from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';

const ProcessTimeline = () => {
  const timelines = [
    {
      pathway: 'Skilled Independent (189)',
      stages: [
        { step: 'Skills Assessment', duration: '2-4 months', description: 'Obtain positive skills assessment from designated assessing authority' },
        { step: 'Submit EOI via SkillSelect', duration: '1-2 weeks', description: 'Create and submit Expression of Interest with all details and documents' },
        { step: 'Receive Invitation to Apply', duration: 'Varies', description: 'Wait for invitation based on points score and occupation (can be 1-12+ months)' },
        { step: 'Lodge Visa Application', duration: '60 days', description: 'Submit complete application within 60 days of invitation' },
        { step: 'Application Processing', duration: '6-12 months', description: 'Department processes application including health and character checks' },
        { step: 'Visa Grant', duration: '1-2 weeks', description: 'Receive visa grant notification and make initial entry to Australia' }
      ],
      totalTime: '12-24 months',
      color: 'from-blue-500 to-blue-600'
    },
    {
      pathway: 'State Nominated (190/491)',
      stages: [
        { step: 'Skills Assessment', duration: '2-4 months', description: 'Obtain positive skills assessment from designated authority' },
        { step: 'State Nomination Application', duration: '2-12 weeks', description: 'Apply for state nomination (varies by state)' },
        { step: 'Receive State Nomination', duration: 'Varies by state', description: 'State reviews and approves nomination' },
        { step: 'Submit EOI & Receive ITA', duration: '1-4 weeks', description: 'Submit EOI with nomination and receive invitation' },
        { step: 'Lodge Visa Application', duration: '60 days', description: 'Submit complete visa application with all documents' },
        { step: 'Application Processing', duration: '8-12 months', description: 'Department processes visa application' },
        { step: 'Visa Grant', duration: '1-2 weeks', description: 'Receive visa grant and plan relocation' }
      ],
      totalTime: '12-18 months',
      color: 'from-green-500 to-green-600'
    },
    {
      pathway: 'Employer Sponsored (186)',
      stages: [
        { step: 'Secure Job Offer', duration: 'Varies', description: 'Obtain job offer from approved Australian employer sponsor' },
        { step: 'Skills Assessment', duration: '2-4 months', description: 'Complete skills assessment for nominated occupation' },
        { step: 'Employer Nomination', duration: '1-3 months', description: 'Employer lodges nomination application with Department' },
        { step: 'Lodge Visa Application', duration: 'After nomination', description: 'Submit visa application after nomination approval' },
        { step: 'Application Processing', duration: '6-9 months', description: 'Department processes visa including checks' },
        { step: 'Visa Grant', duration: '1-2 weeks', description: 'Receive visa grant and commence employment' }
      ],
      totalTime: '12-18 months',
      color: 'from-purple-500 to-purple-600'
    }
  ];

  return (
    <section className="py-16 bg-[#F5F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Standard Processing Timelines
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Understanding the typical timeline for each pathway helps you plan your immigration journey effectively.
          </p>
        </motion.div>

        {/* Timelines */}
        <div className="space-y-12">
          {timelines.map((timeline, tIndex) => (
            <motion.div
              key={tIndex}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: tIndex * 0.2 }}
              className="bg-white rounded-lg shadow-sm p-6 md:p-8"
            >
              {/* Timeline Header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-xl font-semibold text-[#202356] mb-2">
                    {timeline.pathway}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock size={16} />
                    <span className="font-light">Total Processing Time: <strong>{timeline.totalTime}</strong></span>
                  </div>
                </div>
              </div>

              {/* Timeline Steps */}
              <div className="relative">
                {/* Vertical Line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                {/* Steps */}
                <div className="space-y-8">
                  {timeline.stages.map((stage, sIndex) => (
                    <motion.div
                      key={sIndex}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: sIndex * 0.1 }}
                      className="relative pl-12"
                    >
                      {/* Step Number */}
                      <div className={`absolute left-0 w-8 h-8 rounded-full bg-gradient-to-br ${timeline.color} flex items-center justify-center text-white text-sm font-semibold`}>
                        {sIndex + 1}
                      </div>

                      {/* Step Content */}
                      <div className="bg-[#F5F8FA] p-4 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-base font-semibold text-[#202356]">
                            {stage.step}
                          </h4>
                          <span className="text-xs font-light text-[#2542D4] bg-white px-3 py-1 rounded-full whitespace-nowrap ml-4">
                            {stage.duration}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm font-light leading-relaxed">
                          {stage.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Important Notes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 grid md:grid-cols-2 gap-6"
        >
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h4 className="text-base font-semibold text-[#202356] mb-3">
              Processing Time Factors
            </h4>
            <ul className="space-y-2 text-sm text-gray-700 font-light">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Completeness and quality of application documents</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Skills assessment processing times vary by authority</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>State nomination times differ significantly by state</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Department workload and priority processing</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h4 className="text-base font-semibold text-[#202356] mb-3">
              How We Help
            </h4>
            <ul className="space-y-2 text-sm text-gray-700 font-light">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Ensure application completeness to avoid delays</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Guide you through skills assessment process</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Advise on best state nomination options</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Monitor application and respond to requests promptly</span>
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessTimeline;
